import type { FormElement, RadioGroupProperties } from '../../model/form.types';
import type { FieldDefinition, FieldValue, SelectOption, ValidationIssue } from '../../model/types';
import {
  isEmptyValue,
  normalizeOption,
  optionIssue,
  requiredIssue,
  singleColumn,
  stringShapeIssue,
} from './shared';

type RadioGroupElement = FormElement & { properties: RadioGroupProperties };

// The builder stores radio options as bare strings with no id, so the answer is the option
// text itself, as in the dropdown.
function options(element: RadioGroupElement): SelectOption[] {
  return (element.properties.options ?? []).map((option) => ({ value: option, label: option }));
}

function isKnownOption(element: RadioGroupElement, value: string): boolean {
  return options(element).some((option) => normalizeOption(option.value) === normalizeOption(value));
}

function shapeIssue(element: RadioGroupElement, value: FieldValue): ValidationIssue | null {
  return stringShapeIssue(element, value);
}

export const radioGroupDefinition: FieldDefinition<RadioGroupElement> = {
  kind: 'field',
  type: 'RADIO_GROUP',
  emptyValue: () => '',
  control: (element) => ({ kind: 'radio', options: options(element) }),
  shapeIssue,
  validate: (element, value) => {
    const shape = shapeIssue(element, value);
    if (shape) return [shape];
    if (typeof value !== 'string' || isEmptyValue(value)) {
      return element.properties.required ? [requiredIssue(element)] : [];
    }
    return isKnownOption(element, value) ? [] : [optionIssue(element)];
  },
  columns: singleColumn,
  cells: (_element, value) => [typeof value === 'string' ? value : ''],
  triggerMatches: (_element, value, option) =>
    typeof value === 'string' && value.length > 0 && normalizeOption(value) === normalizeOption(option),
};
